import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X, Loader2, Info } from 'lucide-react';
import { invalidateOtaCache } from '../../hooks/useOtaOptions';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OTA Options Management — Settings → Module Settings → Hotel Onboarded.
 * --------------------------------------------------------------------------
 * Admin CRUD for the master list of OTAs that powers the OTA Name dropdown
 * inside Accounts → Add Account / Edit Account → OTA Information.
 *
 * Backend surface: /api/ota-options  (GET / POST / PUT / DELETE)
 *
 * Deletion here NEVER touches historical account records — it only removes
 * the OTA from the dropdown for new selections. Renames are similarly
 * scoped to the config; historical records keep their stored value and are
 * surfaced back to Edit Account via useOtaOptions's `historicalValues`.
 */
const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const OtaOptionsManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newOpt, setNewOpt] = useState({ label: '', value: '' });
  const [editing, setEditing] = useState(null); // { id, label, value }

  const fetchRows = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`${API}/api/ota-options`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.data || []);
    } catch (_e) {
      setError('Could not load OTA options');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const create = async () => {
    const label = (newOpt.label || '').trim();
    const value = (newOpt.value || '').trim() || label;
    if (!label) { setError('OTA name is required'); return; }
    setBusyId('__new__'); setError('');
    try {
      const res = await fetch(`${API}/api/ota-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ label, value }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || `HTTP ${res.status}`);
      setNewOpt({ label: '', value: '' });
      setAdding(false);
      invalidateOtaCache();
      await fetchRows();
    } catch (e) {
      setError(String(e?.message || e));
    } finally { setBusyId(null); }
  };

  const save = async () => {
    if (!editing?.id) return;
    const label = (editing.label || '').trim();
    const value = (editing.value || '').trim() || label;
    if (!label) { setError('OTA name is required'); return; }
    setBusyId(editing.id); setError('');
    try {
      const res = await fetch(`${API}/api/ota-options/${editing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ label, value }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || `HTTP ${res.status}`);
      setEditing(null);
      invalidateOtaCache();
      await fetchRows();
    } catch (e) {
      setError(String(e?.message || e));
    } finally { setBusyId(null); }
  };

  const del = async (id, label) => {
    if (!window.confirm(`Delete "${label}"? Existing accounts that use it will remain unchanged.`)) return;
    setBusyId(id); setError('');
    try {
      const res = await fetch(`${API}/api/ota-options/${id}`, {
        method: 'DELETE', headers: authHeader(),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${res.status}`);
      }
      invalidateOtaCache();
      await fetchRows();
    } catch (e) {
      setError(String(e?.message || e));
    } finally { setBusyId(null); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-6" data-testid="ota-options-management">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">OTA Configuration</h2>
          <p className="text-xs text-gray-500 mt-1">
            Master OTA list used by the Add Account &amp; Edit Account OTA dropdowns.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setEditing(null); setError(''); }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
            data-testid="ota-add-button"
          >
            <Plus size={14} /> Add OTA
          </button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 my-3 flex items-start gap-2">
        <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-800">
          Deleting an OTA only removes it from the dropdown for new selections.
          Existing accounts that already reference it keep their saved value intact.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded mb-3" data-testid="ota-error">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Display Name</th>
              <th className="text-left px-3 py-2 font-medium text-gray-700">Stored Value</th>
              <th className="text-right px-3 py-2 font-medium text-gray-700 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="bg-teal-50/40 border-b border-gray-100">
                <td className="px-3 py-2">
                  <input
                    autoFocus
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                    placeholder="e.g. Yatra"
                    value={newOpt.label}
                    onChange={e => setNewOpt(v => ({ ...v, label: e.target.value }))}
                    data-testid="ota-new-label"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 outline-none text-sm font-mono"
                    placeholder="optional — mirrors Display Name"
                    value={newOpt.value}
                    onChange={e => setNewOpt(v => ({ ...v, value: e.target.value }))}
                    data-testid="ota-new-value"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={create}
                    disabled={busyId === '__new__'}
                    className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                    title="Save"
                    data-testid="ota-new-save"
                  >
                    {busyId === '__new__' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  </button>
                  <button
                    onClick={() => { setAdding(false); setNewOpt({ label: '', value: '' }); setError(''); }}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded ml-1"
                    title="Cancel"
                    data-testid="ota-new-cancel"
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            )}
            {loading ? (
              <tr><td colSpan="3" className="px-3 py-6 text-center text-gray-400">
                <Loader2 size={16} className="animate-spin inline mr-1" /> Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="3" className="px-3 py-6 text-center text-gray-500">
                No OTA options yet. Click <em>Add OTA</em> to create one.
              </td></tr>
            ) : rows.map(r => {
              const isEditing = editing?.id === r.id;
              return (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`ota-row-${r.id}`}>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <input
                        autoFocus
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                        value={editing.label}
                        onChange={e => setEditing(v => ({ ...v, label: e.target.value }))}
                        data-testid={`ota-edit-label-${r.id}`}
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{r.label}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">
                    {isEditing ? (
                      <input
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 outline-none text-sm font-mono"
                        value={editing.value}
                        onChange={e => setEditing(v => ({ ...v, value: e.target.value }))}
                        data-testid={`ota-edit-value-${r.id}`}
                      />
                    ) : r.value}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEditing ? (
                      <>
                        <button
                          onClick={save}
                          disabled={busyId === r.id}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Save"
                          data-testid={`ota-save-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        </button>
                        <button
                          onClick={() => { setEditing(null); setError(''); }}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded ml-1"
                          title="Cancel"
                          data-testid={`ota-cancel-${r.id}`}
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditing({ id: r.id, label: r.label, value: r.value }); setAdding(false); setError(''); }}
                          className="p-1.5 text-teal-600 hover:bg-teal-50 rounded"
                          title="Edit"
                          data-testid={`ota-edit-${r.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => del(r.id, r.label)}
                          disabled={busyId === r.id}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded ml-1 disabled:opacity-50"
                          title="Delete"
                          data-testid={`ota-delete-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OtaOptionsManagement;
